import React from "react";
import PropTypes from "prop-types";
import { Box, Typography, TextField } from "@mui/material";

export const OrderDetailsSection = ({
  opportunity,
  data = {},
  onDataChange,
  disabled = false,
  showErrors = false,
}) => {
  const productId =
    data.productId ||
    data.productID ||
    opportunity?.productId ||
    opportunity?.productID ||
    opportunity?.scriptCoProductOptionId ||
    opportunity?.grmProductOptionId ||
    "";

  const productQuantity =
    data.productQuantity !== undefined
      ? data.productQuantity
      : opportunity?.productQuantity ||
        opportunity?.minimumOrderQty ||
        opportunity?.metricQty ||
        "";

  const isProductQtyInvalid =
    productQuantity === "" ||
    productQuantity === null ||
    productQuantity === undefined ||
    Number(productQuantity) <= 0;
  const showProductQtyError =
    (showErrors || (productQuantity !== "" && Number(productQuantity) <= 0)) &&
    isProductQtyInvalid;

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (onDataChange) {
      onDataChange({
        ...data,
        [name]: value,
      });
    }
  };

  const handleProductQuantityChange = (e) => {
    let val = e.target.value;
    if (val !== "") {
      const num = Number(val);
      if (Number.isNaN(num) || num < 0) {
        val = 0;
      }
    }
    if (onDataChange) {
      onDataChange({
        ...data,
        productQuantity: val,
      });
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: "#FFFFFF",
        borderRadius: "12px",
        p: 2.5,
        border: "1px solid #E8E8E9",
        display: "flex",
        flexDirection: "column",
        gap: 2.5,
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      <Typography sx={{ fontWeight: 600, fontSize: "16px", color: "#403C6F" }}>
        Order Details
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {/* ProductId & ProductQuantity (Row) */}
        <Box sx={{ display: "flex", gap: 2 }}>
          <Box
            sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 0.5 }}
          >
            <Typography
              sx={{ fontSize: "13px", color: "#403C6F", fontWeight: 500 }}
            >
              Product ID
            </Typography>
            <TextField
              disabled
              variant="outlined"
              size="small"
              value={productId}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#F9FAFB",
                  borderRadius: "8px",
                  "& fieldset": { borderColor: "#E8E8E9" },
                },
                "& .MuiInputBase-input.Mui-disabled": {
                  fontSize: "14px",
                  py: 1.5,
                  color: "#66638C",
                  WebkitTextFillColor: "#66638C",
                },
              }}
            />
          </Box>

          <Box
            sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 0.5 }}
          >
            <Typography
              sx={{ fontSize: "13px", color: "#403C6F", fontWeight: 500 }}
            >
              Product Quantity
            </Typography>
            <TextField
              name="productQuantity"
              type="number"
              variant="outlined"
              size="small"
              disabled={disabled}
              value={productQuantity}
              onChange={handleProductQuantityChange}
              onKeyDown={(e) => {
                if (e.key === "-" || e.key === "e") {
                  e.preventDefault();
                }
              }}
              error={showProductQtyError}
              helperText={showProductQtyError ? "please enter >0" : ""}
              slotProps={{
                htmlInput: { min: 0 },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: disabled ? "#F9FAFB" : "#FFFFFF",
                  borderRadius: "8px",
                  "& fieldset": {
                    borderColor: showProductQtyError ? "#d32f2f" : "#E8E8E9",
                  },
                },
                "& .MuiInputBase-input": {
                  fontSize: "14px",
                  py: 1.5,
                  color: "#0e1324",
                },
                "& .MuiInputBase-input.Mui-disabled": {
                  fontSize: "14px",
                  py: 1.5,
                  color: "#66638C",
                  WebkitTextFillColor: "#66638C",
                },
              }}
            />
          </Box>
        </Box>

        {/* OrderComment */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography
            sx={{ fontSize: "13px", color: "#403C6F", fontWeight: 500 }}
          >
            Order Comment
          </Typography>
          <TextField
            name="orderComment"
            variant="outlined"
            size="small"
            multiline
            rows={2}
            disabled={disabled}
            value={data.orderComment || ""}
            onChange={handleChange}
            placeholder="Enter Order Comment"
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: disabled ? "#F9FAFB" : "#FFFFFF",
                borderRadius: "8px",
                "& fieldset": { borderColor: "#E8E8E9" },
              },
              "& .MuiInputBase-input": {
                fontSize: "14px",
                py: 1,
                color: disabled ? "#66638C" : "#0e1324",
                WebkitTextFillColor: disabled ? "#66638C" : "#0e1324",
              },
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

OrderDetailsSection.propTypes = {
  opportunity: PropTypes.object,
  data: PropTypes.object,
  onDataChange: PropTypes.func,
  disabled: PropTypes.bool,
  showErrors: PropTypes.bool,
};

export const ShippingDetailsSection = () => null;

ShippingDetailsSection.propTypes = {};

const ScriptCoContactPreferences = ({
  type,
  opportunity,
  data = {},
  onDataChange,
}) => {
  if (type === "order") {
    return (
      <OrderDetailsSection
        opportunity={opportunity}
        data={data}
        onDataChange={onDataChange}
      />
    );
  }
  if (type === "shipping") {
    return null;
  }
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <OrderDetailsSection
        opportunity={opportunity}
        data={data}
        onDataChange={onDataChange}
      />
      <ShippingDetailsSection
        opportunity={opportunity}
        data={data}
        onDataChange={onDataChange}
      />
    </Box>
  );
};

ScriptCoContactPreferences.propTypes = {
  type: PropTypes.string,
  opportunity: PropTypes.object,
  data: PropTypes.object,
  onDataChange: PropTypes.func,
};

export default ScriptCoContactPreferences;
